import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import type { DocumentStatus, DocumentSummary } from '../types';

type FolderRef = { id: string; name: string };
type CaseRef = {
  id: string;
  caseNumber: string;
  area: string | null;
  client?: { id: string; name: string } | null;
};
type VersionRow = {
  id: string;
  version: number;
  size: number;
  mime: string;
  uploadedAt: string;
};
type DocFull = DocumentSummary & {
  versions: VersionRow[];
  folder: FolderRef | null;
};

const STATUS_CLASS: Record<DocumentStatus, string> = {
  draft: 'b-pending',
  template: 'b-pending',
  pending_signature: 'b-pending',
  signed: 'b-open',
  archived: 'b-closed',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Read-only info modal for a Document. Renders ownership (client + case),
 * folder, status, area, full version history with per-version download
 * links. Mutating actions (Send, Sign, Delete) stay on the row's action
 * buttons — this modal is just the X-ray view.
 */
export function DocumentInfoModal({
  document: docSummary,
  onClose,
}: {
  document: DocumentSummary;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['documents', 'cases', 'common']);
  const navigate = useNavigate();
  const [doc, setDoc] = useState<DocFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<DocFull>(`/api/documents/${docSummary.id}`)
      .then(setDoc)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [docSummary.id]);

  async function downloadVersion(version: number) {
    const res = await apiFetch<{ url: string }>(
      `/api/documents/${docSummary.id}/download-url?version=${version}`,
    );
    window.open(res.url, '_blank', 'noopener');
  }

  const lang = i18n.language;
  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString(lang, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });

  const caseRef: CaseRef | null = doc?.case ?? docSummary.case ?? null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="form-preview-name">{docSummary.name}</div>
            <div className="form-preview-type">
              v{docSummary.currentVersion} ·{' '}
              {docSummary.latestVersion ? formatBytes(docSummary.latestVersion.size) : '—'}
            </div>
          </div>
          <span
            className={`badge ${STATUS_CLASS[docSummary.status]}`}
            style={{ marginInlineEnd: 8 }}
          >
            {t(`statuses.${docSummary.status}`)}
          </span>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        <div className="form-section">
          <div className="section-label">{t('documents:info.section_owner')}</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 14,
              marginTop: 4,
            }}
          >
            <div>
              <div className="metric-label">{t('documents:info.client')}</div>
              {caseRef?.client ? (
                <a
                  className="btn-link-light"
                  style={{ color: 'var(--accent-mid)', cursor: 'pointer' }}
                  onClick={() => {
                    onClose();
                    navigate(`/clients/${caseRef.client!.id}`);
                  }}
                >
                  {caseRef.client.name}
                </a>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>
              )}
            </div>
            <div>
              <div className="metric-label">{t('documents:info.case')}</div>
              {caseRef ? (
                <a
                  className="btn-link-light"
                  style={{ color: 'var(--accent-mid)', cursor: 'pointer' }}
                  onClick={() => {
                    onClose();
                    navigate(`/cases/${caseRef.id}`);
                  }}
                >
                  {caseRef.caseNumber}
                  {caseRef.area ? ` · ${caseRef.area}` : ''}
                </a>
              ) : (
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>
              )}
            </div>
            <div>
              <div className="metric-label">{t('documents:info.folder')}</div>
              <span style={{ fontSize: 13 }}>
                {doc?.folder?.name ?? <span style={{ color: 'var(--text3)' }}>—</span>}
              </span>
            </div>
            <div>
              <div className="metric-label">{t('documents:info.area')}</div>
              <span style={{ fontSize: 13 }}>
                {docSummary.area ?? <span style={{ color: 'var(--text3)' }}>—</span>}
              </span>
            </div>
            <div>
              <div className="metric-label">{t('documents:info.created')}</div>
              <span style={{ fontSize: 13 }}>{fmtDateTime(docSummary.createdAt)}</span>
            </div>
            <div>
              <div className="metric-label">{t('documents:info.updated')}</div>
              <span style={{ fontSize: 13 }}>{fmtDateTime(docSummary.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('documents:info.versions')}</div>
          {loading ? (
            <div className="empty">{t('common:actions.loading')}</div>
          ) : error ? (
            <div className="empty" style={{ color: 'var(--red)' }}>
              {error}
            </div>
          ) : !doc?.versions.length ? (
            <div className="empty">{t('documents:info.no_versions')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {doc.versions.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      v{v.version}
                      {v.version === docSummary.currentVersion && (
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--accent-mid)',
                            marginInlineStart: 6,
                          }}
                        >
                          {t('documents:info.current')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {fmtDateTime(v.uploadedAt)} · {formatBytes(v.size)} · {v.mime}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => void downloadVersion(v.version)}
                  >
                    {t('documents:info.download')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
